import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import { rightToWorkCheck, storeUserData } from "../../redux/actions";

import DocumentType from "../../components/RightToWork/DocumentType";
import AddSelfie from "../../components/RightToWork/AddSelfie";
import AddDocument from "../../components/RightToWork/AddDocument";
import Verification from "../../components/RightToWork/Verification";
import Yoti from "../../components/RightToWork/Yoti";
import StudentDocuments from "../../components/RightToWork/StudentDocuments";
import * as style from "./styles.module.scss";
import YotiCheckSuccessful from "../../components/YotiCheckSuccessful";
import YotiCheckFailed from "../../components/YotiCheckFailed";

const documentsOptions = [
  {
    value: "passport",
    label: "In date UK or Irish passport, or Irish ID card",
  },
  {
    value: "expiredPassport",
    label: "Expired UK or Irish passport, or Irish ID card",
  },
  { value: "shareCode", label: "Share code" },
  { value: "birthCertificate", label: "Birth/Adoption Certificate" },
  { value: "other", label: "Other" },
];

const useRightToWorkState = ({
  idType = "",
  selfieFile = null,
  frontOfIdFile = null,
  backOfIdFile = null,
  shareCodeFile = null,
  studentPermissions = null,
  courseCompleted = null,
}) => {
  const initialState = {
    idType,
    selfieFile,
    frontOfIdFile,
    backOfIdFile,
    shareCodeFile,
    studentPermissions,
    courseCompleted,
  };

  const [rightToWorkState, setRightToWorkState] = useState(initialState);

  const resetFiles = (value) => {
    setRightToWorkState({
      ...rightToWorkState,
      idType: value,
      frontOfIdFile: null,
      backOfIdFile: null,
      shareCodeFile: null,
      studentPermissions: false,
      courseCompleted: false,
    });
  };

  const onChange = (key, val) => {
    if (key === "idType" && rightToWorkState.idType !== val) {
      return resetFiles(val);
    }
    return setRightToWorkState((state) => ({
      ...state,
      [key]: val,
    }));
  };

  return [rightToWorkState, onChange];
};

const hints = [
  {
    name: "closeUp",
    label: "The photo must be a close up picture.",
    src: "/images/photo-camera.svg",
  },
  {
    name: "jpgOrPng",
    label: "The image should be a JPEG or PNG file.",
    src: "/images/rtw_images.svg",
  },
  {
    name: "approve",
    label:
      "Ensure the photo is recent and clear - we will not be able to approve your right to work documents without this photo.",
    src: "/images/rtw_face.svg",
  },
  {
    name: "notPassportPhoto",
    label: "This can not be a picture of your passport photo.",
    src: "/images/no_passport_photo.svg",
  },
  {
    name: "signedPassport",
    label: "The passport should be signed.",
    src: "/images/rtw_signed.svg",
  },
  {
    name: "signature",
    label: "The picture should clearly show the signature.",
    src: "/images/rtw_signature.svg",
  },
  {
    name: "passportPhoto",
    label: "The picture should show the passport photo.",
    src: "/images/rtw_photo.svg",
  },
  {
    name: "corners",
    label: "All 4 corners of the page should be shown.",
    src: "/images/rtw_corners.svg",
  },
  {
    name: "passportNumbers",
    label: "The passport number should be clearly displayed.",
    src: "/images/rtw_numbers.svg",
  },
  {
    name: "visaNumber",
    label: "The Visa number should be clearly displayed.",
    src: "/images/rtw_numbers.svg",
  },
  {
    name: "niNumber",
    label: "The NI document should have the number visible.",
    src: "/images/rtw_numbers.svg",
  },
  {
    name: "fingers",
    label: "There can’t be glare or fingers over the text.",
    src: "/images/rtw_fingers.svg",
  },
  {
    name: "visaFrontAndBack",
    label: "The front and back of the Visa should be visible.",
    src: "/images/rtw_document.svg",
  },
  {
    name: "idFrontAndBack",
    label: "The front and back of the ID should be visible.",
    src: "/images/rtw_document.svg",
  },
  {
    name: "documentsFront",
    label: "The front of the documents should be visible.",
    src: "/images/rtw_document.svg",
  },
  {
    name: "noPayslip",
    label: "The NI document can’t be a payslip.",
    src: "/images/rtw_crossed.svg",
  },
  {
    name: "possession",
    label: "Please ensure you have this document in your possession.",
    src: "/images/cardinhand.jpeg",
  },
];

const hintsByFile = {
  addSelfie: ["closeUp", "jpgOrPng", "approve", "notPassportPhoto"],
  passport: [
    "signedPassport",
    "signature",
    "jpgOrPng",
    "fingers",
    "passportPhoto",
    "corners",
    "passportNumbers",
    "possession",
  ],
  visaPassport: ["closeUp", "fingers", "corners", "visaNumber", "possession"],
  birthCertificate: [
    "closeUp",
    "jpgOrPng",
    "fingers",
    "documentsFront",
    "corners",
    "niNumber",
    "noPayslip",
    "possession",
  ],
  other: [
    "closeUp",
    "jpgOrPng",
    "fingers",
    "documentsFront",
    "corners",
    "possession",
  ],
  studentDocuments: [
    "closeUp",
    "jpgOrPng",
    "fingers",
    "documentsFront",
    "corners",
    "possession",
  ],
};

const documents = [
  {
    id: "passport",
    name: "UK & EEA Passport",
    title: "Add the picture & information page of your passport",
    hints: hints.filter((hint) => hintsByFile.passport.includes(hint.name)),
    subtitles: [],
  },
  {
    id: "expiredPassport",
    name: "Expired UK or Irish Passport",
    title:
      "2. Upload a copy of your expired UK or Irish passport or Irish ID card (JPEG or PNG)",
    hints: hints.filter((hint) => hintsByFile.passport.includes(hint.name)),
    subtitles: [],
  },
  {
    id: "visaPassport",
    name: "VISA page of your passport",
    title:
      "Add the Visa page and the picture & information page of your passport",
    hints: hints.filter((hint) => hintsByFile.visaPassport.includes(hint.name)),
    subtitles: [
      "The VISA page of your passport",
      "The picture & information page of your passport",
    ],
  },
  {
    id: "birthCertificate",
    name: "Birth/Adoption Certificate",
    title:
      "Please upload the front of your birth/adoption certificate and the front of your national insurance document (JPEG or PNG)",
    hints: hints.filter((hint) =>
      hintsByFile.birthCertificate.includes(hint.name)
    ),
    subtitles: [
      "2. The front of your birth/adoption certificate",
      "3. The front of your national insurance document",
    ],
  },
  {
    id: "shareCode",
    name: "Share code",
    title: "Add your share code",
    hints: [],
    subtitles: [],
  },
  {
    id: "other",
    name: "Other",
    title:
      "Please upload the front of your ID document and optional second image (JPEG or PNG)",
    hints: [],
    subtitles: ["2. Image of your ID document", "3. Optional second image"],
  },
  {
    id: "studentDocuments",
    name: "Student Documents",
    title:
      "Please upload evidence that you are enrolled, on which course and the relevant term dates (this may be in 1 or 2 supporting documents. JPEG or PNG)",
    hints: hints.filter((hint) =>
      hintsByFile.studentDocuments.includes(hint.name)
    ),
    subtitles: ["1. University letter confirming enrolment", "2. Term dates"],
  },
  {
    id: "studentCompletedDocuments",
    name: "Student Documents",
    title:
      "Please upload proof that your course is completed e.g. a certificate of qualification or letter from the University confirming completion",
    hints: hints.filter((hint) =>
      hintsByFile.studentDocuments.includes(hint.name)
    ),
    subtitles: [
      "1. Proof that your course has been completed",
      "2. University letter confirming enrolment",
    ],
  },
];

const renderStep = (
  step,
  setStep,
  previousStep,
  setPreviousStep,
  state,
  handleChange,
  onSubmit,
  rightToWorkFiles,
  responsibleTeam,
  jobId,
  findStep,
  scrollToTop,
  nextStepWhenCompleted,
  setRTWVerified
) => {
  const getHints = (docName) => {
    return hints.filter(
      (hint) => hintsByFile[docName] && hintsByFile[docName].includes(hint.name)
    );
  };
  const changeStep = (newStep, stepBefore = "") => {
    setStep(newStep);
    setPreviousStep(stepBefore);
    scrollToTop();
  };

  switch (step) {
    case 0:
      return (
        <DocumentType
          setStep={changeStep}
          idType={state.idType}
          studentPermissions={state.studentPermissions}
          courseCompleted={state.courseCompleted}
          documents={documentsOptions}
          handleChange={handleChange}
          rightToWorkFiles={rightToWorkFiles}
          jobId={jobId}
          findStep={findStep}
        />
      );
    case 1:
      return (
        <AddSelfie
          step={step}
          setStep={changeStep}
          previousStep={previousStep}
          handleChange={handleChange}
          file={state.selfieFile}
          hints={getHints("addSelfie")}
        />
      );
    case 2:
      return (
        <AddDocument
          idType={state.idType}
          step={step}
          setStep={changeStep}
          handleChange={handleChange}
          frontOfIdFile={state.frontOfIdFile}
          backOfIdFile={state.backOfIdFile}
          document={
            documents.filter((document) => document.id === state.idType)[0]
          }
          hints={getHints(state.idType)}
          onSubmit={onSubmit}
        />
      );
    case 3:
      return (
        <Verification
          step={step}
          setStep={changeStep}
          rtwFiles={rightToWorkFiles}
          documentType={state.idType}
          showBack={!(rightToWorkFiles.length > 1)}
          responsibleTeam={responsibleTeam}
          returnBackLink={findStep("health-and-disability", jobId)}
          nextStepLink={nextStepWhenCompleted()}
        />
      );
    case "yoti":
      return <Yoti jobId={jobId} findStep={findStep} />;
    case "success":
      return (
        <YotiCheckSuccessful
          nextStepLink={nextStepWhenCompleted()}
          jobId={jobId}
          findStep={findStep}
          setRTWVerified={setRTWVerified}
        />
      );
    case "error":
      return (
        <YotiCheckFailed
          nextStepLink={nextStepWhenCompleted()}
          jobId={jobId}
          findStep={findStep}
        />
      );
    case "studentCompleted":
      return (
        <StudentDocuments
          courseCompleted={state.courseCompleted}
          idType={state.idType}
          step={step}
          setStep={changeStep}
          setPreviousStep={setPreviousStep}
          handleChange={handleChange}
          frontOfIdFile={state.frontOfIdFile}
          backOfIdFile={state.backOfIdFile}
          document={documents.find(
            (document) => document.id === "studentCompletedDocuments"
          )}
          hints={hints.filter((hint) =>
            hintsByFile.studentDocuments.includes(hint.name)
          )}
          onSubmit={onSubmit}
        />
      );
    case "student":
      return (
        <StudentDocuments
          courseCompleted={state.courseCompleted}
          idType={state.idType}
          step={step}
          setStep={changeStep}
          setPreviousStep={setPreviousStep}
          handleChange={handleChange}
          frontOfIdFile={state.frontOfIdFile}
          backOfIdFile={state.backOfIdFile}
          document={documents.find(
            (document) => document.id === "studentDocuments"
          )}
          hints={hints.filter((hint) =>
            hintsByFile.studentDocuments.includes(hint.name)
          )}
          onSubmit={onSubmit}
        />
      );
    default:
      return "Oops, something went wrong";
  }
};

const isRtwImage = ({ TypeCode }) =>
  !["NAPP", "WRTV", "WRTF", "YRTW", "YRTF"].includes(TypeCode);

const setInitialProcessStep = (
  rightToWorkFiles,
  rightToWorkVerified,
  rightToWorkVerifiedInFONT,
  section
) => {
  if (rightToWorkVerified || rightToWorkVerifiedInFONT) {
    return 3;
  }

  if (section === "success" || section === "error") {
    return section;
  }

  if (
    rightToWorkFiles.find(({ TypeCode }) => TypeCode === "YRTF") &&
    !rightToWorkFiles.find(({ TypeCode }) => TypeCode === "YRTW")
  ) {
    return 0;
  }

  if (rightToWorkFiles.find(({ TypeCode }) => TypeCode === "JPPT")) {
    return 0;
  }

  if (
    rightToWorkFiles.filter(isRtwImage).length === 2 &&
    (rightToWorkFiles.find(({ TypeCode }) => TypeCode === "JBIR") ||
      rightToWorkFiles.find(({ TypeCode }) => TypeCode === "JPNI"))
  ) {
    return 0;
  }

  return rightToWorkFiles.filter(isRtwImage).length > 1 ? 3 : 0;
};

const RightToWorkContainer = ({
  appData,
  triggerRTWCheck,
  rightToWorkFiles = [],
  rightToWorkVerified,
  rightToWorkVerifiedInFONT,
  jobId,
  findStep,
  nextStepWhenCompleted,
  section,
  updateAppState,
}) => {
  const [state, handleChange] = useRightToWorkState({});
  const [step, setStep] = useState(
    setInitialProcessStep(
      rightToWorkFiles,
      rightToWorkVerified,
      rightToWorkVerifiedInFONT,
      section
    )
  );

  const setRTWVerified = (flag) => {
    const updatedState = { ...appData, rightToWorkVerifiedInFONT: flag };
    updateAppState(updatedState);
  };

  const [previousStep, setPreviousStep] = useState("");

  const containerRef = useRef(null);
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const onSubmit = () => {
    triggerRTWCheck({
      ...state,
    });
  };

  return (
    <div
      ref={containerRef}
      className={`col-sm-12 col-lg-6 mt-2 mt-lg-4 mb-2 ${style.form}`}
    >
      {renderStep(
        step,
        setStep,
        previousStep,
        setPreviousStep,
        state,
        handleChange,
        onSubmit,
        rightToWorkFiles,
        appData.responsibleTeam,
        jobId,
        findStep,
        scrollToTop,
        nextStepWhenCompleted,
        setRTWVerified
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  appData: state.core.app,
  rightToWorkFiles: state.core.app.rightToWorkFiles,
  rightToWorkVerified: state.core.app.rightToWorkVerified,
  rightToWorkVerifiedInFONT: state.core.app.rightToWorkVerifiedInFONT,
});

const mapDispatchToProps = {
  triggerRTWCheck: rightToWorkCheck,
  updateAppState: storeUserData,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(RightToWorkContainer);
